/**
 * API contracts shared by the NestJS API (request validation) and the admin panel (forms, types).
 * Browser-safe: no Node or Prisma imports. Enum values mirror packages/database/prisma/schema.prisma.
 */
import { z } from 'zod';
// ───────────────────────────── Enums ─────────────────────────────
export const LANGUAGES = ['EN', 'GU', 'HI'];
export const ADMIN_ROLES = ['SUPER_ADMIN', 'ZONE_ADMIN', 'AGENT', 'EDITOR'];
export const CONSENT_STATUSES = ['OPTED_IN', 'OPTED_OUT'];
export const CONVERSATION_STATUSES = ['BOT', 'QUEUED', 'ASSIGNED', 'CLOSED'];
export const KB_KINDS = ['LINK', 'ANSWER', 'SERVICE'];
export const KB_SOURCES = [
    'SCRAPED_PAGE',
    'SCRAPED_PDF',
    'SCRAPED_LINK',
    'EXISTING_BOT',
    'MANUAL',
];
export const TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
export const TEMPLATE_STATUSES = [
    'DRAFT',
    'PENDING',
    'APPROVED',
    'REJECTED',
    'PAUSED',
    'DISABLED',
];
export const BROADCAST_STATUSES = [
    'DRAFT',
    'SCHEDULED',
    'SENDING',
    'COMPLETED',
    'CANCELLED',
    'FAILED',
];
export const DELIVERY_STATUSES = [
    'RECEIVED',
    'QUEUED',
    'SENT',
    'DELIVERED',
    'READ',
    'FAILED',
];
export const UNANSWERED_STATUSES = ['OPEN', 'RESOLVED', 'IGNORED'];
export const MESSAGE_DIRECTIONS = ['INBOUND', 'OUTBOUND'];
export const SENDER_TYPES = ['CITIZEN', 'BOT', 'AGENT', 'SYSTEM'];
export const MESSAGE_TYPES = [
    'TEXT',
    'AUDIO',
    'IMAGE',
    'VIDEO',
    'DOCUMENT',
    'LOCATION',
    'INTERACTIVE',
    'TEMPLATE',
    'STICKER',
    'UNKNOWN',
];
export const COMPLAINT_STATUSES = [
    'OPEN',
    'IN_PROGRESS',
    'RESOLVED',
    'REJECTED',
    'CLOSED',
];
/** Background jobs: citizen imports and scrape runs. */
export const JOB_STATUSES = ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED'];
export const LANGUAGE_LABELS = {
    EN: 'English',
    GU: 'ગુજરાતી',
    HI: 'हिंदी',
};
/** What each role may do. The API enforces this; the admin panel uses it to hide UI. */
export const PERMISSIONS = {
    'dashboard:view': ['SUPER_ADMIN', 'ZONE_ADMIN', 'AGENT', 'EDITOR'],
    'citizens:read': ['SUPER_ADMIN', 'ZONE_ADMIN', 'AGENT'],
    'citizens:write': ['SUPER_ADMIN', 'ZONE_ADMIN'],
    'citizens:import': ['SUPER_ADMIN', 'ZONE_ADMIN'],
    'divisions:write': ['SUPER_ADMIN'],
    'kb:read': ['SUPER_ADMIN', 'EDITOR', 'ZONE_ADMIN', 'AGENT'],
    'kb:write': ['SUPER_ADMIN', 'EDITOR'],
    'templates:read': ['SUPER_ADMIN', 'EDITOR', 'ZONE_ADMIN'],
    'templates:write': ['SUPER_ADMIN', 'EDITOR'],
    'broadcasts:read': ['SUPER_ADMIN', 'ZONE_ADMIN'],
    'broadcasts:send': ['SUPER_ADMIN', 'ZONE_ADMIN'],
    'chats:handle': ['SUPER_ADMIN', 'ZONE_ADMIN', 'AGENT'],
    'users:manage': ['SUPER_ADMIN'],
    'settings:manage': ['SUPER_ADMIN'],
    'audit:read': ['SUPER_ADMIN'],
};
export const can = (role, permission) => PERMISSIONS[permission].includes(role);
// ───────────────────────────── Common ─────────────────────────────
const uuid = z.uuid();
const optionalUuid = z.uuid().optional();
const trimmed = (max) => z.string().trim().min(1).max(max);
/** Trims and lower-cases first: zod 4 checks `z.email()`'s format before a chained `.trim()`. */
const email = () => z.string().trim().toLowerCase().pipe(z.email());
/** Query-string booleans ("true"/"false"). */
const queryBool = z.enum(['true', 'false']).transform((v) => v === 'true');
export const paginationQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** WhatsApp ID: E.164 digits, no '+'. Accepts common Indian formats and normalises them. */
export const waIdSchema = z
    .string()
    .trim()
    .transform((s) => s.replace(/[\s\-()+]/g, ''))
    .transform((s) => (/^[6-9]\d{9}$/.test(s) ? `91${s}` : s.replace(/^0(?=[6-9]\d{9}$)/, '91')))
    .pipe(z.string().regex(/^\d{10,15}$/, 'Phone must be 10–15 digits (e.g. 9876543210 or 919876543210)'));
// ───────────────────────────── Auth ─────────────────────────────
export const loginSchema = z.object({
    email: email(),
    password: z.string().min(1).max(200),
});
export const createAdminUserSchema = z
    .object({
    email: email(),
    name: trimmed(100),
    password: z.string().min(10, 'At least 10 characters').max(200),
    role: z.enum(ADMIN_ROLES),
    zoneId: optionalUuid.nullable(),
})
    .refine((u) => u.role !== 'ZONE_ADMIN' || u.zoneId, {
    message: 'Zone admins need a zone',
    path: ['zoneId'],
});
export const updateAdminUserSchema = z.object({
    name: trimmed(100).optional(),
    password: z.string().min(10).max(200).optional(),
    role: z.enum(ADMIN_ROLES).optional(),
    zoneId: optionalUuid.nullable(),
    isActive: z.boolean().optional(),
});
// ───────────────────────────── Divisions ─────────────────────────────
export const zoneSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9_]{2,20}$/),
    nameEn: trimmed(100),
    nameGu: z.string().trim().max(100).nullish(),
    nameHi: z.string().trim().max(100).nullish(),
});
export const updateZoneSchema = zoneSchema.partial();
export const wardSchema = z.object({
    zoneId: uuid,
    number: z.coerce.number().int().min(1).max(999),
    nameEn: trimmed(100),
    nameGu: z.string().trim().max(100).nullish(),
    nameHi: z.string().trim().max(100).nullish(),
});
export const updateWardSchema = wardSchema.partial();
export const areaSchema = z.object({
    wardId: uuid,
    nameEn: trimmed(100),
    nameGu: z.string().trim().max(100).nullish(),
    nameHi: z.string().trim().max(100).nullish(),
});
export const updateAreaSchema = areaSchema.partial();
/**
 * Body of POST /api/divisions/wards/boundaries: a GeoJSON FeatureCollection (e.g. exported from
 * QGIS / VMC GIS). `?property=` names the feature property that holds the ward number.
 */
export const wardBoundaryImportSchema = z.object({
    type: z.literal('FeatureCollection'),
    features: z.array(z.object({
        properties: z.record(z.string(), z.unknown()).nullable(),
        geometry: z.object({
            type: z.enum(['Polygon', 'MultiPolygon']),
            coordinates: z.array(z.unknown()),
        }),
    })),
});
// ───────────────────────────── Citizens ─────────────────────────────
/**
 * Tags are stored lowercase with single spaces, so "VIP", "vip " and "Vip" are one tag and every
 * tag filter ignores case.
 */
export const normalizeTag = (t) => t.trim().replace(/\s+/g, ' ').toLowerCase();
const tagList = z
    .array(z.string().trim().min(1).max(50).transform(normalizeTag))
    .transform((tags) => [...new Set(tags)]);
/** Audience / list filter. Shared by the citizen list, export, and broadcast audiences. */
export const citizenFilterSchema = z.object({
    search: z.string().trim().max(100).optional(),
    zoneIds: z.array(uuid).optional(),
    wardIds: z.array(uuid).optional(),
    areaIds: z.array(uuid).optional(),
    tags: tagList.optional(),
    languages: z.array(z.enum(LANGUAGES)).optional(),
    consentStatus: z.enum(CONSENT_STATUSES).optional(),
    /** Only citizens with no ward yet (to find who needs tagging). */
    unassigned: z.boolean().optional(),
});
/** Same filter from a query string: comma-separated lists ("a,b,c"). */
const splitCsv = (v) => typeof v === 'string'
    ? v
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
    : v;
export const citizenQuerySchema = paginationQuery.extend({
    search: z.string().trim().max(100).optional(),
    zoneIds: z.preprocess(splitCsv, z.array(uuid)).optional(),
    wardIds: z.preprocess(splitCsv, z.array(uuid)).optional(),
    areaIds: z.preprocess(splitCsv, z.array(uuid)).optional(),
    tags: z.preprocess(splitCsv, tagList).optional(),
    languages: z.preprocess(splitCsv, z.array(z.enum(LANGUAGES))).optional(),
    consentStatus: z.enum(CONSENT_STATUSES).optional(),
    unassigned: queryBool.optional(),
});
export const updateCitizenSchema = z.object({
    name: z.string().trim().max(100).nullish(),
    language: z.enum(LANGUAGES).optional(),
    zoneId: optionalUuid.nullable(),
    wardId: optionalUuid.nullable(),
    areaId: optionalUuid.nullable(),
    address: z.string().trim().max(300).nullish(),
    tags: tagList.pipe(z.array(z.string()).max(30)).optional(),
    voiceReplies: z.boolean().optional(),
});
export const createCitizenSchema = updateCitizenSchema.extend({ waId: waIdSchema });
/** Columns accepted by the citizen import (CSV/XLSX header row, case-insensitive). */
export const CITIZEN_IMPORT_COLUMNS = [
    'phone',
    'name',
    'language',
    'ward',
    'area',
    'address',
    'tags',
];
// ─────────────────────────── Knowledge base ───────────────────────────
export const kbLinkSchema = z.object({ label: trimmed(60), url: z.url() });
export const kbContentSchema = z.object({
    what: z.string().trim().max(1000).optional(),
    steps: z.array(z.string().trim().min(1).max(200)).max(10).optional(),
    phone: z.object({ label: trimmed(60), number: trimmed(30) }).nullish(),
    links: z.array(kbLinkSchema).max(5).optional(),
});
/** KB entry fields without defaults, so the PATCH schema can't fill in values the client left out. */
const kbEntryFields = z.object({
    kind: z.enum(KB_KINDS),
    title: trimmed(200),
    url: z.url().nullish(),
    keywords: z.array(z.string().trim().min(1).max(100)).min(1).max(100),
    emoji: z.string().max(8).nullish(),
    content: kbContentSchema.nullish(),
    priority: z.number().int().min(-100).max(100),
    isActive: z.boolean(),
});
export const kbEntrySchema = kbEntryFields.extend({
    priority: kbEntryFields.shape.priority.default(0),
    isActive: kbEntryFields.shape.isActive.default(true),
});
export const kbUpdateSchema = kbEntryFields.partial();
export const kbTranslationSchema = z.object({
    title: trimmed(200),
    content: kbContentSchema.nullish(),
});
export const kbQuerySchema = paginationQuery.extend({
    search: z.string().trim().max(100).optional(),
    kind: z.enum(KB_KINDS).optional(),
    source: z.enum(KB_SOURCES).optional(),
    active: queryBool.optional(),
});
export const botTestSchema = z.object({
    text: trimmed(500),
    lang: z.enum(LANGUAGES).default('EN'),
});
export const unansweredQuerySchema = paginationQuery.extend({
    status: z.enum(UNANSWERED_STATUSES).default('OPEN'),
});
export const unansweredUpdateSchema = z.object({
    status: z.enum(UNANSWERED_STATUSES),
    resolvedEntryId: optionalUuid.nullable(),
});
// ─────────────────────────── Templates & broadcasts ───────────────────────────
/** Meta rules: lowercase letters, digits, underscores. */
export const templateNameSchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9_]{1,512}$/, 'Lowercase letters, digits and _ only');
export const templateButtonSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('URL'), text: trimmed(25), url: z.url() }),
    z.object({
        type: z.literal('PHONE_NUMBER'),
        text: trimmed(25),
        phone: z.string().regex(/^\+?\d{8,15}$/),
    }),
    z.object({ type: z.literal('QUICK_REPLY'), text: trimmed(25) }),
]);
export const templateTranslationSchema = z.object({
    language: z.enum(LANGUAGES),
    header: z.string().trim().max(60).nullish(),
    /** Variables as {{1}}, {{2}} … (Meta positional format). */
    body: trimmed(1024),
    footer: z.string().trim().max(60).nullish(),
    buttons: z.array(templateButtonSchema).max(3).nullish(),
});
/** Template fields without defaults, so the PATCH schema can't fill in values the client left out. */
const templateFields = z.object({
    name: templateNameSchema,
    category: z.enum(TEMPLATE_CATEGORIES),
    description: z.string().trim().max(300).nullish(),
    variables: z
        .array(z.object({ key: z.string().regex(/^\d+$/), label: trimmed(50), example: trimmed(100) }))
        .max(10),
    translations: z.array(templateTranslationSchema).min(1),
});
export const templateSchema = templateFields.extend({
    category: templateFields.shape.category.default('UTILITY'),
    variables: templateFields.shape.variables.default([]),
});
export const updateTemplateSchema = templateFields.partial();
export const broadcastSchema = z.object({
    name: trimmed(150),
    templateId: uuid,
    variables: z.record(z.string(), z.string().max(500)).default({}),
    audience: citizenFilterSchema,
    scheduledAt: z.iso.datetime().nullish(),
    isEmergency: z.boolean().default(false),
});
export const audiencePreviewSchema = z.object({ audience: citizenFilterSchema });
// ─────────────────────────── Handoff chats ───────────────────────────
export const conversationQuerySchema = paginationQuery.extend({
    status: z.enum(CONVERSATION_STATUSES).optional(),
    mine: queryBool.optional(),
});
/** Message history paging: `before` = createdAt of the oldest message already shown. */
export const historyQuerySchema = z.object({
    before: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const agentReplySchema = z.object({ text: trimmed(4000) });
export const assignSchema = z.object({ agentId: uuid });
export const cannedReplySchema = z.object({
    shortcut: z
        .string()
        .trim()
        .regex(/^\/[a-z0-9_-]{1,30}$/, 'Like /hours'),
    title: trimmed(100),
    bodyEn: trimmed(2000),
    bodyGu: z.string().trim().max(2000).nullish(),
    bodyHi: z.string().trim().max(2000).nullish(),
});
export const updateCannedReplySchema = cannedReplySchema.partial();
// ───────────────────────────── Settings ─────────────────────────────
export const botStringsSchema = z.record(z.string(), z.string().max(4000));
// ───────────────────────────── Audit ─────────────────────────────
export const auditQuerySchema = paginationQuery.extend({
    entityType: z.string().max(50).optional(),
});
//# sourceMappingURL=index.js.map